import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserDocumentsRoutes } from 'src/app/core/enums/routes.enum';
import { DepartmentApiService } from 'src/app/department/services/department-api.service';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  getExternalDocumentIdFromPath,
  getLinkToDocumentByPublicationState,
} from '../functions/get-link-to-document';
import { IDocument } from '../models/document.model';
import { DocumentApiService } from './document-api.service';

export interface ExternalDocumentRedirectResult {
  path: string | null | undefined;
  departmentId: string | null;
}

const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class ExternalDocumentRedirectService {
  constructor(
    private documentApiService: DocumentApiService,
    private departmentApiService: DepartmentApiService,
    private store: Store
  ) {}

  resolve(
    path: string | null | undefined,
    departmentId: string
  ): Observable<ExternalDocumentRedirectResult> {
    const documentId = getExternalDocumentIdFromPath(path);
    if (!documentId) {
      return of({ path, departmentId: this.resolveProvidedDepartmentId(departmentId) });
    }

    return this.documentApiService.loadDocumentById(documentId, true).pipe(
      switchMap((document) =>
        this.resolveDepartmentId(document, departmentId).pipe(
          map((resolvedDepartmentId) =>
            this.buildDocumentRedirectResult(document, departmentId, resolvedDepartmentId)
          )
        )
      ),
      catchError((error) => {
        console.error('Error resolving external document link:', error);
        return of({ path: UserDocumentsRoutes.Main, departmentId: null });
      })
    );
  }

  private resolveProvidedDepartmentId(departmentId: string): string | null {
    return this.isZeroDepartmentId(departmentId) ? null : departmentId;
  }

  private resolveDepartmentId(
    document: IDocument,
    departmentId: string
  ): Observable<string | null> {
    if (!this.isZeroDepartmentId(departmentId)) {
      return of(departmentId);
    }

    if (document.departmentSpecific) {
      return of(this.pickFirstMatchingDepartment(this.getDepartmentIds(document.departments)));
    }

    return this.resolveMenuDepartmentId(document);
  }

  private resolveMenuDepartmentId(document: IDocument): Observable<string | null> {
    if (!document.menu?.id) {
      return of(null);
    }

    return this.departmentApiService
      .getAllDepartmentsForMenuId(document.menu.id, true)
      .pipe(
        map((departments) => this.pickFirstMatchingDepartment(this.getDepartmentIds(departments)))
      );
  }

  private buildDocumentRedirectResult(
    document: IDocument,
    departmentId: string,
    resolvedDepartmentId: string | null
  ): ExternalDocumentRedirectResult {
    if (this.isZeroDepartmentId(departmentId) && !resolvedDepartmentId) {
      return { path: UserDocumentsRoutes.Main, departmentId: null };
    }

    const { link, referrerType } = getLinkToDocumentByPublicationState(document);
    return {
      path: `${link}?referrerType=${encodeURIComponent(referrerType)}`,
      departmentId: resolvedDepartmentId,
    };
  }

  private pickFirstMatchingDepartment(departmentIds: string[]): string | null {
    const userDepartments = this.store.selectSnapshot(
      TenantUserAccessState.getAllAvailableDepartments
    );

    return userDepartments.find((department) => departmentIds.includes(department.id))?.id ?? null;
  }

  private getDepartmentIds(departments: any[] | undefined): string[] {
    return (departments ?? [])
      .map((department) => (typeof department === 'string' ? department : department?.id))
      .filter((id): id is string => typeof id === 'string');
  }

  private isZeroDepartmentId(id: string): boolean {
    return id === ZERO_UUID;
  }
}
